import { useState } from 'react'
import { useAllBadges } from '../hooks/useHunt'
import { BadgeCard, BadgeDetailModal } from '../components/Badge'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { BottomNav } from '../components/common/BottomNav'
import type { BadgeWithProgress, BadgeRarity } from '../types'

const T = { sora: "'Sora', sans-serif", inter: "'Inter', sans-serif" }

type Filter = 'All' | 'Earned' | 'Locked' | 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary'

const FILTERS: Filter[] = [
  'All',
  'Earned',
  'Locked',
  'Common',
  'Uncommon',
  'Rare',
  'Epic',
  'Legendary',
]

const FILTER_TO_RARITY: Partial<Record<Filter, BadgeRarity>> = {
  Common: 'common',
  Uncommon: 'uncommon',
  Rare: 'rare',
  Epic: 'epic',
  Legendary: 'legendary',
}

export default function Badges() {
  const { data, isLoading, error } = useAllBadges()
  const [selected, setSelected] = useState<BadgeWithProgress | null>(null)
  const [filter, setFilter] = useState<Filter>('All')

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-card p-6 text-center shadow-card">
          <h2 className="text-xl font-semibold text-brown-900 mb-2">Failed to load badges</h2>
          <p className="text-brown-600">Please try again later.</p>
        </div>
      </div>
    )
  }

  const badges: BadgeWithProgress[] = data?.badges ?? []
  const total = data?.total ?? badges.length
  const earnedCount = badges.filter((b) => !!b.earned_at).length
  const earnedPoints = badges
    .filter((b) => !!b.earned_at)
    .reduce((sum, b) => sum + (b.points_value ?? 0), 0)
  const progressPct = total > 0 ? (earnedCount / total) * 100 : 0

  const filtered = badges.filter((b) => {
    if (filter === 'All') return true
    if (filter === 'Earned') return !!b.earned_at
    if (filter === 'Locked') return !b.earned_at
    const r = FILTER_TO_RARITY[filter]
    return r ? b.rarity === r : true
  })

  return (
    <div className="min-h-screen bg-cream-50 pb-24" style={{ fontFamily: T.inter }}>
      {/* Header */}
      <div
        style={{
          background: 'linear-gradient(160deg,#92400e,#78350f)',
          padding: '20px 20px 18px',
        }}
      >
        <div className="max-w-lg mx-auto">
          <div style={{ marginBottom: 12 }}>
            <h1
              style={{
                fontFamily: T.sora,
                fontWeight: 700,
                fontSize: 24,
                color: '#fff',
                letterSpacing: '-0.025em',
              }}
            >
              Badges
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <span
                style={{
                  fontSize: 12,
                  color: '#fde68a',
                  fontFamily: T.inter,
                  fontWeight: 500,
                }}
              >
                Earned {earnedCount} of {total}
              </span>
              <span
                style={{
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  background: '#fde68a',
                  opacity: 0.6,
                  display: 'block',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: '#fde68a',
                  fontFamily: T.inter,
                  fontWeight: 700,
                }}
              >
                {earnedPoints} pts
              </span>
            </div>
          </div>

          <div
            style={{
              height: 5,
              borderRadius: 9999,
              background: 'rgba(255,255,255,.15)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 9999,
                width: `${progressPct}%`,
                background: 'linear-gradient(to right,#fbbf24,#d97706)',
                transition: 'width .4s ease',
              }}
            />
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div
        style={{
          padding: '12px 20px 8px',
          background: '#fff',
          borderBottom: '1px solid #fef3c7',
          boxShadow: '0 2px 8px rgba(120,53,15,.06)',
        }}
      >
        <div
          className="max-w-lg mx-auto scrollbar-hide"
          style={{ display: 'flex', gap: 6, overflowX: 'auto' }}
        >
          {FILTERS.map((f) => {
            const active = filter === f
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  flexShrink: 0,
                  padding: '6px 13px',
                  borderRadius: 9999,
                  fontFamily: T.inter,
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: 'none',
                  transition: 'all .15s ease',
                  background: active ? '#d97706' : 'transparent',
                  color: active ? '#fff' : '#8c5f3a',
                  outline: active ? 'none' : '1.5px solid #fde68a',
                  boxShadow: active ? '0 2px 6px rgba(217,119,6,.35)' : 'none',
                  minHeight: 0,
                }}
              >
                {f}
              </button>
            )
          })}
        </div>
      </div>

      {/* Grid */}
      <main className="max-w-lg mx-auto" style={{ padding: '14px 16px 80px' }}>
        {filtered.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
            }}
          >
            {filtered.map((badge) => (
              <BadgeCard key={badge.id} badge={badge} onClick={() => setSelected(badge)} />
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 0',
              color: '#a8a29e',
              fontFamily: T.inter,
              fontSize: 14,
            }}
          >
            No badges in this category yet.
          </div>
        )}
      </main>

      {selected && <BadgeDetailModal badge={selected} onClose={() => setSelected(null)} />}

      <BottomNav />
    </div>
  )
}
